/**
 * Veranda-configurator - gedeelde kern
 * Version: 3.0 - tenant-laag + transport-abstractie
 *
 * Deze kern is merkloos. Wat per klant verschilt (merk, taal, endpoints,
 * render-transport, productregels, tracking) staat in tenant.config.js.
 * 
 * VIEWMODE SYSTEM:
 * ----------------
 * ViewMode represents different camera viewpoints for the 3D renders:
 * - ViewMode 0: Day view (default)
 * - ViewMode 1: Night view
 * - ViewMode 2-X: Custom camera angles (e.g., side view, detail view)
 * 
 * The system supports setting custom viewModes per feature. Example:
 * - When selecting side walls → use viewMode 4 for better side angle
 * - When selecting roof → use viewMode 2 for overhead view
 * 
 * Use setCustomViewMode(number) to override the default day/night behavior
 * Use resetViewMode() to return to day/night toggle behavior
 * 
 * PRICING SYSTEM:
 * ---------------
 * Pricing data is fetched from Google Sheets via backend API
 * Data is cached in frontend for performance
 */

import pricingService from './pricing-service.js';
import PricingCalculator from './pricing-calculator.js';
import { loadConfigurationFromURL, generateShareLink, copyShareLink, captureRenderImage } from './share-link.js';
import i18n from './i18n.js';
import QRCode from 'qrcode';
import tenant from './tenant.config.js';
import {
    buildRequestString, requestRender, requestUpgrade, cancelRenders,
    warmupRenderer, hasQualityTiers,
} from './render/index.js';

/**
 * Google Tag Manager tracking helper
 * Pushes events to dataLayer for GTM tracking
 * @param {string} eventName - GTM event name
 * @param {object} payload - Event data
 */
function trackGTM(eventName, payload = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: eventName,
        timestamp: new Date().toISOString(),
        language: i18n.getCurrentLanguage(),
        ...payload
    });
}

// Global pricing calculator instance
let pricingCalculator = null;

/* ========================================
   STATE MANAGEMENT
   ======================================== */

const state = {
    activeStep: 1,
    houseType: '2',  // Always Vrijstaand (2)
    width: 6,
    depth: 3,
    verandaType: '0',  // 0=Standaard, 1=Pergamon
    color: '383e42',
    finishingCap: '0',
    covering: '0',     // 0=Opaal polycarbonaat (standaard), 1=Helder polycarbonaat, 2=Glas
    led: '0',          // 0=Nee (standaard kale overkapping), 1=Ja
    leftWall: '0',     // 0=geen, 1=glas, 2=aluminium schutting/lambrisering, 3=spies, 4=glaswand met spies
    rightWall: '0',    // 0=geen, 1=glas, 2=aluminium schutting/lambrisering, 3=spies, 4=glaswand met spies
    frontWall: '0',    // 0=geen, 1=glaswand
    markiezen: false,  // Extra optie (geen prijs impact)
    screens: false,    // Extra optie (geen prijs impact)
    vasteKozijnen: false, // Extra optie (geen prijs impact)
    night: false,
    viewMode: 0, // Camera standpunt: 0=dag, 1=nacht, 2-X=andere camera posities
    customViewMode: null, // Override viewMode voor specifieke features (bijv. zijwanden)
    showLoader: false,
    requestString: '',
    lastRequestedString: '', // Track last requested image to prevent race conditions
    pollingTries: 0,         // alleen zichtbaar bij een transport dat pollt
    currentLoadingString: '',// welke render nu in het <img> geladen wordt
    pricingDataLoaded: false, // Track if pricing data is loaded
    pricingError: null, // Track pricing errors
    renderFailed: false, // Track if last render failed/timed out
    lastSuccessfulRender: null, // Track last successful render string
    // Quote modal session id — incremented on each open/close so stale
    // hub-callbacks from a previous modal-open bail out.
    quotePollingChainId: 0,
    // Tracks which requestString currently has its image being loaded into
    // the quote modal so we don't reassign src twice for the same render.
    quoteImageLoadingFor: null
};

/* ========================================
   INITIALIZATION
   ======================================== */

document.addEventListener('DOMContentLoaded', async function () {

    const loader = document.getElementById('loader');
    if (loader) {
        loader.classList.remove('show');
    }

    // Check for shared configuration in URL
    const urlConfig = loadConfigurationFromURL();
    if (urlConfig) {
        Object.assign(state, urlConfig);
        // Voert de klant pergamon maar in één kleur, corrigeer dan een
        // share-link die per ongeluk een ongeldige combinatie bevat.
        if (state.verandaType === '1' && tenant.product.pergamonForcesColor) {
            state.color = tenant.product.pergamonForcesColor;
        }
    }

    bindEvents();
    initScrollHeader();

    // Initialize wizard form
    initWizardForm();

    // Move wizard stepper to widget on mobile
    moveStepperToWidgetOnMobile();
    window.addEventListener('resize', moveStepperToWidgetOnMobile);

    // Zet de verbinding met de render-laag alvast op, zodat die handshake
    // parallel aan het ophalen van de prijzen loopt. Duurt hij langer, dan
    // wacht de eerste render-aanvraag er intern op.
    warmupRenderer();

    // Load pricing data from backend FIRST
    await loadPricingData();

    // Wait a bit to ensure data is fully loaded
    await new Promise(resolve => setTimeout(resolve, 100));

    // Calculate initial price on page load with correct data
    calculatePrice();

    // Set initial viewMode and update navigation buttons
    state.viewMode = 0; // Default front view
    updateNavigationButtons();

    // If URL config loaded, update UI to reflect it
    if (urlConfig) {
        updateAllInputsFromState();
        // Recalculate price with loaded config
        calculatePrice();
        // Then generate image (with slight delay to ensure UI is ready)
        setTimeout(() => generateImage(), 500);
    } else {
        // Generate initial image on page load (only if no URL config to avoid double render)
        generateImage();
    }
});

/* ========================================
   PRICING DATA LOADING
   ======================================== */

async function loadPricingData() {
    try {
        await pricingService.fetchPricingData();
        state.pricingDataLoaded = true;
        state.pricingError = null;

        // Initialize pricing calculator with Opties data
        const optiesData = pricingService.pricingData?.['Opties'];
        if (optiesData) {
            pricingCalculator = new PricingCalculator(optiesData);
        }

    } catch (error) {
        state.pricingDataLoaded = false;
        state.pricingError = error.message;
    }
}

/* ========================================
   SCROLL HEADER
   ======================================== */

function initScrollHeader() {
    const navbar = document.querySelector('.navbar');
    const navBack = document.querySelector('.nav-back');

    if (!navbar || !navBack) return;

    window.addEventListener('scroll', function () {
        // Only make header solid on desktop; keep transparent on mobile
        if (window.innerWidth >= 1024) {
            if (window.scrollY > 0) {
                navBack.classList.add('visible');
            } else {
                navBack.classList.remove('visible');
            }
        } else {
            navBack.classList.remove('visible');
        }
    });
}

/* ========================================
   EVENT BINDING
   ======================================== */

function bindEvents() {

    // Hero button - scroll with header offset
    const heroBtn = document.getElementById('hero-btn');
    if (heroBtn) {
        heroBtn.addEventListener('click', function () {
            // GTM Event: Configurator Start
            trackGTM('configurator_start', {
                source: 'hero_button'
            });

            const configuratorEl = document.getElementById('configurator');
            if (configuratorEl) {
                const headerHeight = 60; // Navbar height
                const elementPosition = configuratorEl.getBoundingClientRect().top + window.pageYOffset;
                const offsetPosition = elementPosition - headerHeight;

                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
            }
        });
    }

    // Step headers
    document.querySelectorAll('.step-header').forEach(button => {
        button.addEventListener('click', function () {
            const step = parseInt(this.dataset.step);
            toggleStep(step);
        });
    });

    // Form inputs
    bindFormInputs();

    // Day/Night toggle
    bindDayNightToggle();

    // Calculate button
    const calculateBtn = document.getElementById('calculate-btn');
    if (calculateBtn) {
        calculateBtn.addEventListener('click', calculatePrice);
    }

    // Mobile menu
    initMobileMenu();

    // Language switcher
    initLanguageSwitcher();

    // Quote modal
    initQuoteModal();

    // Active page highlighting
    highlightActivePage();
}

function bindFormInputs() {
    // House type selection
    document.querySelectorAll('input[name="houseType"]').forEach(input => {
        input.addEventListener('change', function () {
            if (this.checked) {
                state.houseType = this.value;
                autoSelectViewMode('houseType');
                calculatePrice();
                openStep(2);
            }
        });
    });

    // Width input
    const widthInput = document.getElementById('width-input');
    if (widthInput) {
        widthInput.addEventListener('change', function () {
            state.width = Math.max(3, Math.min(12, parseFloat(this.value) || 6));
            this.value = state.width;
            autoSelectViewMode('width');
            calculatePrice();
        });
    }

    // Depth input
    const depthInput = document.getElementById('depth-input');
    if (depthInput) {
        depthInput.addEventListener('change', function () {
            state.depth = Math.max(2.5, Math.min(5, parseFloat(this.value) || 3));
            this.value = state.depth;
            autoSelectViewMode('depth');
            calculatePrice();
        });
    }

    // Other configuration options with auto viewMode selection
    ['verandaType', 'color', 'finishingCap', 'covering', 'led', 'leftWall', 'rightWall', 'frontWall'].forEach(name => {
        document.querySelectorAll(`input[name="${name}"]`).forEach(input => {
            input.addEventListener('change', function () {
                if (this.checked) {
                    state[name] = this.value;

                    // Voert de klant pergamon in één kleur, zet de kleur dan
                    // terug zodra iemand vanuit een andere kleur op pergamon
                    // klikt; anders vraagt de render een variant die niet bestaat.
                    const forced = tenant.product.pergamonForcesColor;
                    if (name === 'verandaType' && this.value === '1' && forced && state.color !== forced) {
                        state.color = forced;
                        updateAllInputsFromState();
                    }

                    // Automatically select best viewMode for this configuration option
                    autoSelectViewMode(name);

                    // Update price automatically
                    calculatePrice();
                }
            });
        });
    });
}

function bindDayNightToggle() {
    const dayBtn = document.getElementById('day-btn');
    const nightBtn = document.getElementById('night-btn');

    // Wisselen tussen dag en nacht houdt het camerastandpunt vast waar de
    // bezoeker naar kijkt (linkerwand, bovenaanzicht, gootvorm). We rekenen
    // customViewMode om naar zijn dag- of nacht-tegenhanger in plaats van hem
    // op null te zetten; dat laatste sprong terug naar de voorkant, ook als
    // iemand net op een detail was ingezoomd.
    function switchDayNight(toNight) {
        state.night = toNight;
        if (state.customViewMode !== null) {
            state.customViewMode = getViewModeForDayNight(state.customViewMode);
        }
        updateDayNightButtons();
        updateNavigationButtons();
        generateImage();
    }

    if (dayBtn) dayBtn.addEventListener('click', () => switchDayNight(false));
    if (nightBtn) nightBtn.addEventListener('click', () => switchDayNight(true));

    // De ruimte tussen de twee knoppen is groot genoeg om ernaast te klikken.
    // Een klik daar wisselt gewoon naar de andere stand.
    const toggleContainer = dayBtn ? dayBtn.closest('.daynight-toggle') : null;
    if (toggleContainer) {
        toggleContainer.addEventListener('click', (e) => {
            if (e.target.closest('.daynight-btn')) return; // knoppen doen het zelf
            switchDayNight(!state.night);
        });
        toggleContainer.style.cursor = 'pointer';
    }
}

/* ========================================
   STEP MANAGEMENT
   ======================================== */

function toggleStep(step) {
    const stepElement = document.getElementById(`step-${step}`);
    const stepContent = stepElement.querySelector('.step-content');
    const stepArrow = stepElement.querySelector('.step-arrow');

    // Close all steps
    document.querySelectorAll('.step-content').forEach(content => {
        content.classList.remove('active');
    });
    document.querySelectorAll('.step-arrow').forEach(arrow => {
        arrow.classList.remove('rotate');
    });
    document.querySelectorAll('.config-step').forEach(el => {
        el.classList.remove('active');
    });

    // Toggle current step
    if (state.activeStep === step) {
        state.activeStep = 0;
    } else {
        state.activeStep = step;
        stepContent.classList.add('active');
        stepArrow.classList.add('rotate');
        stepElement.classList.add('active');
    }
}

function openStep(step) {
    state.activeStep = step;
    toggleStep(step);
}

/* ========================================
   UI UPDATES
   ======================================== */

function updateDayNightButtons() {
    // Main buttons
    const dayBtn = document.getElementById('day-btn');
    const nightBtn = document.getElementById('night-btn');

    if (dayBtn && nightBtn) {
        if (state.night) {
            dayBtn.classList.remove('active');
            nightBtn.classList.add('active');
        } else {
            dayBtn.classList.add('active');
            nightBtn.classList.remove('active');
        }
    }

    // Widget buttons
    const widgetDayBtn = document.getElementById('widget-day-btn');
    const widgetNightBtn = document.getElementById('widget-night-btn');

    if (widgetDayBtn && widgetNightBtn) {
        if (state.night) {
            widgetDayBtn.classList.remove('active');
            widgetNightBtn.classList.add('active');
        } else {
            widgetDayBtn.classList.add('active');
            widgetNightBtn.classList.remove('active');
        }
    }
}

function updateUI() {
    const loader = document.getElementById('loader');
    const statusMessage = document.getElementById('status-message');

    if (loader) {
        if (state.showLoader) {
            loader.classList.add('show');
            if (statusMessage) {
                // Bij een transport dat pollt tellen we de pogingen mee, zodat
                // een trage render zichtbaar voortgang houdt in plaats van een
                // stilstaande tekst.
                const bouwen = i18n.t('loader_building') || 'Veranda wordt voor u gebouwd...';
                if (!state.pollingTries) {
                    statusMessage.textContent = bouwen;
                } else if (state.pollingTries < 100) {
                    statusMessage.textContent = `${bouwen} (${state.pollingTries})`;
                } else {
                    statusMessage.textContent = `${i18n.t('loader_patience') || 'Nog even geduld...'} (${state.pollingTries})`;
                }
            }
        } else {
            loader.classList.remove('show');
        }
    }

    // Update navigation buttons visibility based on viewMode
    updateNavigationButtons();
}

/**
 * Update navigation button visibility based on current viewMode
 * Front (0/1): left, right, up
 * Boven (4/9): down, left, right
 * Rechts (6/11): left, up
 * Links (5/10): right, up
 * Detail kap (2/7): geen pijlen
 * Detail glas (3/8): geen pijlen
 */
function updateNavigationButtons() {
    // Use customViewMode if set, otherwise use viewMode
    const currentViewMode = state.customViewMode !== null ? state.customViewMode : state.viewMode;

    // Define which buttons should be visible per viewMode
    const visibilityMap = {
        0: ['left', 'right', 'up'],      // Front day
        1: ['left', 'right', 'up'],      // Front night
        2: [],                            // Detail kap day - geen pijlen
        3: [],                            // Detail glas day - geen pijlen
        4: ['down', 'left', 'right'],    // Boven day
        5: ['right', 'up'],              // Links day
        6: ['left', 'up'],               // Rechts day
        7: [],                            // Detail kap night - geen pijlen
        8: [],                            // Detail glas night - geen pijlen
        9: ['down', 'left', 'right'],    // Boven night
        10: ['right', 'up'],             // Links night
        11: ['left', 'up']               // Rechts night
    };

    const visibleButtons = visibilityMap[currentViewMode] || ['left', 'right', 'up', 'down'];

    // Update main navigation (fullscreen) - use ID selector
    updateNavButtonsForContainer('#main-navigation', visibleButtons);

    // Update widget navigation - use ID selector
    updateNavButtonsForContainer('#widget-navigation', visibleButtons);
}

function updateNavButtonsForContainer(containerSelector, visibleButtons) {
    const container = document.querySelector(containerSelector);
    if (!container) return;

    const buttons = {
        up: container.querySelector('.viewmode-nav-up'),
        down: container.querySelector('.viewmode-nav-down'),
        left: container.querySelector('.viewmode-nav-left'),
        right: container.querySelector('.viewmode-nav-right')
    };

    // Hide all buttons first
    Object.values(buttons).forEach(btn => {
        if (btn) btn.style.display = 'none';
    });

    // Show only visible buttons
    visibleButtons.forEach(direction => {
        if (buttons[direction]) {
            buttons[direction].style.display = 'flex';
        }
    });
}

/* ========================================
   RENDER UNAVAILABLE OVERLAY
   ======================================== */

function showRenderUnavailableOverlay() {
    // Show overlay on main preview
    const mainOverlay = document.getElementById('render-unavailable-overlay');
    if (mainOverlay) {
        mainOverlay.classList.add('visible');
    }

    // Show overlay on widget preview
    const widgetOverlay = document.getElementById('widget-render-unavailable-overlay');
    if (widgetOverlay) {
        widgetOverlay.classList.add('visible');
    }
}

function hideRenderUnavailableOverlay() {
    // Hide overlay on main preview
    const mainOverlay = document.getElementById('render-unavailable-overlay');
    if (mainOverlay) {
        mainOverlay.classList.remove('visible');
    }

    // Hide overlay on widget preview
    const widgetOverlay = document.getElementById('widget-render-unavailable-overlay');
    if (widgetOverlay) {
        widgetOverlay.classList.remove('visible');
    }
}

/* ========================================
   IMAGE GENERATION & RENDERING
   ======================================== */

function generateRequestString(context = 'fast') {
    let widthCm = Math.round(state.width * 100);
    let depthCm = Math.round(state.depth * 100);

    // Apply constraints
    if (widthCm < 300) widthCm = 300;
    if (widthCm > 1200) widthCm = 1200;
    if (depthCm < 250) depthCm = 250;
    if (depthCm > 500) depthCm = 500;

    // Design type: 2 for pergamon, otherwise finishingCap (0=modern, 1=klassiek)
    let designType = state.verandaType == '1' ? '2' : state.finishingCap;

    // Determine viewMode: customViewMode takes precedence, then night/day, then default
    let viewMode = state.customViewMode !== null
        ? state.customViewMode
        : (state.night ? 1 : 0);

    state.viewMode = viewMode; // Update current viewMode in state

    // De opbouw zelf staat in render/index.js: 11 velden, of 17 als deze
    // tenant kwaliteits-tiers gebruikt.
    return buildRequestString({
        houseType: state.houseType,
        designType,
        color: state.color,
        covering: state.covering,
        widthCm,
        depthCm,
        led: state.led,
        leftWall: state.leftWall,
        rightWall: state.rightWall,
        frontWall: state.frontWall,
        viewMode,
    }, context);
}

/**
 * Set custom camera viewpoint for specific features
 * @param {number|null} viewModeNumber - ViewMode number (0-X), or null to use default day/night
 * 
 * Example usage:
 * - setCustomViewMode(4) - Use viewMode 4 for side walls
 * - setCustomViewMode(null) - Reset to default day/night mode
 */
function setCustomViewMode(viewModeNumber) {
    state.customViewMode = viewModeNumber;
    generateImage();
    updateNavigationButtons();  // Update buttons after viewMode change
}

/**
 * ViewMode day/night conversion maps
 */
const viewModeMapping = {
    dayToNight: {
        0: 1,   // front dag → front nacht
        2: 7,   // dakpaneel dag → dakpaneel nacht
        3: 8,   // gootvorm dag → gootvorm nacht
        4: 9,   // bovenaanzicht dag → bovenaanzicht nacht
        5: 10,  // links dag → links nacht
        6: 11   // rechts dag → rechts nacht
    },
    nightToDay: {
        1: 0,   // front nacht → front dag
        7: 2,   // dakpaneel nacht → dakpaneel dag
        8: 3,   // gootvorm nacht → gootvorm dag
        9: 4,   // bovenaanzicht nacht → bovenaanzicht dag
        10: 5,  // links nacht → links dag
        11: 6   // rechts nacht → rechts dag
    }
};

/**
 * Get the appropriate viewMode based on current day/night setting
 */
function getViewModeForDayNight(baseViewMode) {
    if (state.night) {
        return viewModeMapping.dayToNight[baseViewMode] || baseViewMode;
    } else {
        return viewModeMapping.nightToDay[baseViewMode] || baseViewMode;
    }
}

/**
 * Navigate to specific viewMode with direction
 * @param {string} direction - 'up', 'down', 'left', 'right'
 */
function navigateViewMode(direction) {
    const currentBase = state.customViewMode !== null ? state.customViewMode : state.viewMode;

    // Get base day viewMode
    let baseViewMode = viewModeMapping.nightToDay[currentBase] || currentBase;

    let newBaseViewMode;

    // GTM Event: Viewmode Change
    trackGTM('viewmode_change', {
        direction: direction,
        from_viewMode: baseViewMode,
        night: state.night
    });

    switch (direction) {
        case 'up':
            newBaseViewMode = 4; // bovenaanzicht
            break;
        case 'down':
            newBaseViewMode = 0; // front
            break;
        case 'left':
            // Van rechts (6) → front (0) → links (5)
            if (baseViewMode === 6) {
                newBaseViewMode = 0; // rechts naar front
            } else {
                newBaseViewMode = 5; // anders naar links
            }
            break;
        case 'right':
            // Van links (5) → front (0) → rechts (6)
            if (baseViewMode === 5) {
                newBaseViewMode = 0; // links naar front
            } else {
                newBaseViewMode = 6; // anders naar rechts
            }
            break;
        default:
            newBaseViewMode = 0;
    }

    // Apply day/night conversion
    const finalViewMode = getViewModeForDayNight(newBaseViewMode);
    setCustomViewMode(finalViewMode);
    updateNavigationButtons();
}

/**
 * Auto-select best viewMode based on which configuration option changed
 */
function autoSelectViewMode(configOption) {
    let baseViewMode = 0; // default front

    switch (configOption) {
        case 'covering':
            baseViewMode = 2; // dakpaneel zoom
            break;
        case 'finishingCap':
            baseViewMode = 3; // gootvorm detail
            break;
        case 'color':
            baseViewMode = 4; // bovenaanzicht
            break;
        case 'led':
            // LED ja OF nee = automatisch nacht (beide opties tonen nachtcamera)
            state.night = true;
            updateDayNightButtons(); // Update buttons direct
            baseViewMode = 0; // front view
            break;
        case 'leftWall':
            // Altijd linkerzijde camera tonen (ook bij "geen")
            baseViewMode = 5; // linkerzijde
            break;
        case 'rightWall':
            // Altijd rechterzijde camera tonen (ook bij "geen")
            baseViewMode = 6; // rechterzijde
            break;
        case 'frontWall':
            baseViewMode = 0; // front view
            break;
        case 'verandaType':
        case 'houseType':
        case 'width':
        case 'depth':
            baseViewMode = 0; // front view
            break;
        default:
            baseViewMode = 0;
    }

    // Apply day/night conversion
    const finalViewMode = getViewModeForDayNight(baseViewMode);
    setCustomViewMode(finalViewMode);
}

let _generateImageTimer = null;
function generateImage() {
    // Debounce: skip if called again within 500ms
    if (_generateImageTimer) {
        clearTimeout(_generateImageTimer);
    }
    _generateImageTimer = setTimeout(() => {
        _generateImageTimer = null;
        _doGenerateImage();
    }, 100);
}

/* ========================================
   RENDER AANVRAGEN
   ======================================== */
// Welk transport hieronder ligt (de gedeelde hub of de oude PHP-wachtrij)
// bepaalt tenant.config.js. In deze functie is dat niet meer te zien.

function _doGenerateImage() {
    const requestString = generateRequestString('fast');
    const hqString = hasQualityTiers ? generateRequestString('hq') : requestString;

    state.requestString = requestString;
    state.lastRequestedString = requestString;
    state.showLoader = true;
    state.pollingTries = 0;
    // Reset de foutstand bij elk nieuw verzoek, anders blijft een eerdere
    // overlay hangen en telt de teller niet opnieuw vanaf nul.
    state.renderFailed = false;
    state.currentLoadingString = '';
    hideRenderUnavailableOverlay();

    // Wat nog liep en niet meer nodig is, breken we af. Bij de PHP-wachtrij
    // haalt dat de rijen ook echt weg; op de hub schrijven we ons alleen uit.
    cancelRenders([requestString, hqString]);

    updateUI();

    requestRender(requestString, {
        priority: 1, // hier wacht iemand op, dus voorrang op achtergrondwerk
        onProgress: (tries) => {
            if (requestString !== state.lastRequestedString) return;
            state.pollingTries = tries;
            updateUI();
        },
        onReady: (url, rs) => {
            // Er kan intussen een nieuwere render gevraagd zijn; negeer dan.
            if (rs !== state.lastRequestedString) return;
            state.lastSuccessfulRender = rs;
            setPreviewImage(url, rs);

            // De snelle preview staat. Haal daarna stil de mooie versie op en
            // wissel om zodra die binnen is. Zonder kwaliteits-tiers doet dit niets.
            requestUpgrade(hqString, '#preview-image, #widget-preview-image',
                () => state.requestString === requestString);
        },
        onTimeout: (rs) => {
            if (rs !== state.lastRequestedString) return;
            state.showLoader = false;
            state.renderFailed = true;
            showRenderUnavailableOverlay();
            updateUI();
        }
    });
}

function setPreviewImage(url, requestString) {
    const previewImg = document.getElementById('preview-image');
    const widgetImg = document.getElementById('widget-preview-image');

    if (previewImg) {
        previewImg.onload = null;
        previewImg.onerror = null;

        previewImg.onload = () => {
            if (requestString === state.lastRequestedString) {
                state.showLoader = false;
                state.pollingTries = 0;
                state.currentLoadingString = '';
                state.renderFailed = false;
                state.lastSuccessfulRender = requestString;
                hideRenderUnavailableOverlay();
                updateUI();
            }
        };

        previewImg.onerror = () => {
            if (requestString !== state.lastRequestedString) return;
            // De render bestaat (dat is net geprobeerd), dus een laadfout hier
            // betekent meestal dat de jpg nog niet volledig ge-upload was.
            // Eén keer opnieuw met een verse cache-bust, en pas daarna opgeven.
            if (!state.currentLoadingString) {
                state.currentLoadingString = requestString;
                setTimeout(() => {
                    if (requestString !== state.lastRequestedString) return;
                    previewImg.src = url.split('?')[0] + '?t=' + Date.now();
                }, 1500);
                return;
            }
            console.warn(`[Render] image load faalde voor ${requestString} (${url})`);
            state.currentLoadingString = '';
            state.showLoader = false;
            state.renderFailed = true;
            showRenderUnavailableOverlay();
            updateUI();
        };

        previewImg.src = url;
    }

    if (widgetImg) {
        widgetImg.src = url;
    }
}

/* ========================================
   PRICE CALCULATION
   ======================================== */

function calculatePrice() {
    // Reset pricing error flag for fresh calculation
    state.pricingError = false;

    try {
        // Determine which sheet to use based on verandaType
        const sheetName = state.verandaType === '1' ? 'Pergamon' : 'Standaard';

        let basePrice = 0;
        let breakdown = null;

        // Use advanced pricing calculator if available
        if (state.pricingDataLoaded && pricingCalculator) {
            // Get rounded dimensions for base price lookup
            const roundedDims = pricingCalculator.getRoundedDimensions(state.width, state.depth);

            // Get base price from Google Sheets using ROUNDED dimensions
            const price = pricingService.getPrice(sheetName, roundedDims.width, roundedDims.depth);

            if (price !== null) {
                basePrice = price;

                // Calculate all extras using advanced calculator
                breakdown = pricingCalculator.calculate({
                    width: state.width,
                    depth: state.depth,
                    type: sheetName.toLowerCase(),
                    covering: state.covering,
                    led: state.led,
                    leftWall: state.leftWall,
                    rightWall: state.rightWall,
                    frontWall: state.frontWall
                });

            } else {
                basePrice = calculateFallbackPrice();
                state.pricingError = true;
            }
        } else {
            // Simple calculation without advanced features
            const roundedWidth = state.width % 1 > 0 ? Math.ceil(state.width) : state.width;
            const roundedDepth = state.depth % 0.5 > 0 ? Math.ceil(state.depth / 0.5) * 0.5 : state.depth;

            const price = pricingService.getPrice(sheetName, roundedWidth, roundedDepth);
            if (price !== null) {
                basePrice = price;

                // Simple extras calculation
                breakdown = {
                    totalExtras: 0
                };

                // Add inkorten if needed
                if (state.width % 1 > 0 || state.depth % 0.5 > 0) {
                    breakdown.totalExtras += 390 * ((state.width % 1 > 0 ? 1 : 0) + (state.depth % 0.5 > 0 ? 1 : 0));
                }

                // Add LED if enabled
                if (state.led === '1') {
                    const liggers = Math.ceil(state.width) + 1;
                    const ledLiggers = Math.max(0, liggers - 2);
                    breakdown.totalExtras += ledLiggers * 2 * 25;
                }

                // Add glasdak if enabled
                if (state.covering === '2') {
                    breakdown.totalExtras += state.width * state.depth * 140;
                }
            } else {
                basePrice = calculateFallbackPrice();
                state.pricingError = true;
            }
        }

        // Calculate total
        const totalExtras = breakdown ? breakdown.totalExtras : 0;
        const totalPrice = basePrice + totalExtras;

        // Sanity check: smallest veranda (3m×2.5m) costs ~€3000+
        // Price below €2000 means corrupt pricing data from API
        if (totalPrice < 2000) {
            state.pricingError = true;
        }

        // Format prices for display
        const totalPriceFormatted = pricingService.formatPrice(totalPrice);

        // Update price displays
        updateAllPriceDisplays(totalPriceFormatted);

        // Show/hide pricing error warning
        updatePricingErrorWarning(state.pricingError);

    } catch (error) {
        console.error('❌ Error calculating price:', error);
        state.pricingError = true;
        // Fallback to basic calculation
        const fallbackTotal = calculateFallbackPrice();
        const formattedFallback = pricingService.formatPrice(fallbackTotal);

        updateAllPriceDisplays(formattedFallback);
        updatePricingErrorWarning(true);
    }
}

/**
 * Update all price display elements (simple, widget, preview badge)
 */
function updateAllPriceDisplays(formattedPrice) {
    const simpleTotalPriceEl = document.getElementById('simple-total-price');
    if (simpleTotalPriceEl) simpleTotalPriceEl.textContent = formattedPrice;

    const widgetTotalPriceEl = document.getElementById('widget-total-price');
    if (widgetTotalPriceEl) widgetTotalPriceEl.textContent = `€ ${formattedPrice}`;

    // Update preview price badge
    const previewBadgePriceEl = document.getElementById('preview-badge-price');
    if (previewBadgePriceEl) previewBadgePriceEl.textContent = formattedPrice;

    // Update wizard inline price (below wizard steps)
    const wizardInlinePriceEl = document.getElementById('wizard-inline-total-price');
    if (wizardInlinePriceEl) wizardInlinePriceEl.textContent = formattedPrice;
}

/**
 * Show or hide the pricing error warning
 */
function updatePricingErrorWarning(showError) {
    const warningEl = document.getElementById('pricing-error-warning');
    if (warningEl) {
        warningEl.style.display = showError ? 'flex' : 'none';
    }
    // Also update wizard inline pricing error
    const wizardWarningEl = document.getElementById('wizard-pricing-error-warning');
    if (wizardWarningEl) {
        wizardWarningEl.style.display = showError ? 'flex' : 'none';
    }
}

/**
 * Fallback price calculation if Google Sheets data unavailable
 */
function calculateFallbackPrice() {
    let base = 1500;
    const area = state.width * state.depth;

    // Base price calculation
    base += area * 150;
    if (state.verandaType === '1') base += 1200;

    // Extras
    let extras = 0;
    if (state.covering === '2') extras += area * 80;
    if (state.led === '1') extras += 350;

    return base + extras;
}

/* ========================================
   GLOBAL FUNCTIONS
   ======================================== */

// Expose functions to global scope for inline event handlers
window.openStep = openStep;
window.setCustomViewMode = setCustomViewMode; // Voor toekomstig gebruik met specifieke features
window.navigateViewMode = navigateViewMode; // ViewMode navigatie met pijltjes
window.openQuoteModal = openQuoteModal; // Quote modal openen

window.hideLoader = function () {
    state.showLoader = false;
    const loader = document.getElementById('loader');
    if (loader) {
        loader.classList.remove('show');
    }
    updateUI();
};

/**
 * Reset viewMode to default day/night behavior
 * Helper function voor gebruik in de UI
 */
window.resetViewMode = function () {
    setCustomViewMode(null);
};

/**
 * Toggle fullscreen for an element
 */
window.toggleFullscreen = function (elementId) {
    const element = document.getElementById(elementId);
    if (!element) return;

    if (!document.fullscreenElement) {
        element.requestFullscreen().catch(err => {
            console.error(`Error attempting to enable fullscreen: ${err.message}`);
        });
    } else {
        document.exitFullscreen();
    }
};

// Update navigation buttons when entering/exiting fullscreen
document.addEventListener('fullscreenchange', function () {
    // Small delay to ensure DOM is ready
    setTimeout(() => {
        updateNavigationButtons();
    }, 100);
});

// ViewMode tester functions removed - use navigation arrows and automatic selection instead

/* ========================================
   MOBILE MENU
   ======================================== */

/**
 * Twee tenants, twee headers: de Webflow-navbar (.nav-menu-wrapper) en de
 * mimugarten-header (.mg-header). Elke tenant levert er precies één, dus
 * beide blokken staan hier naast elkaar en het blok dat niets vindt doet niets.
 * Een derde klant met een eigen header voegt hier zijn eigen blok toe.
 */
function initMobileMenu() {
    initWebflowNav();
    initMgNav();
}

function initWebflowNav() {
    const menuButton = document.querySelector('.menu-button');
    const navMenu = document.querySelector('.nav-menu-wrapper');
    const overlay = document.querySelector('.w-nav-overlay');

    if (!menuButton || !navMenu || !overlay) return;

    // Toggle menu
    menuButton.addEventListener('click', function (e) {
        e.stopPropagation();
        toggleMobileMenu();
    });

    // Close on overlay click
    overlay.addEventListener('click', function () {
        closeMobileMenu();
    });

    // Close on menu link click
    document.querySelectorAll('.nav-menu-wrapper .menu-link-block').forEach(link => {
        link.addEventListener('click', function () {
            closeMobileMenu();
        });
    });

    // Close on ESC key
    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && navMenu.classList.contains('open')) {
            closeMobileMenu();
        }
    });
}

function toggleMobileMenu() {
    const navMenu = document.querySelector('.nav-menu-wrapper');
    const isOpen = navMenu.classList.contains('open');

    if (isOpen) {
        closeMobileMenu();
    } else {
        openMobileMenu();
    }
}

function openMobileMenu() {
    const menuButton = document.querySelector('.menu-button');
    const navMenu = document.querySelector('.nav-menu-wrapper');
    const overlay = document.querySelector('.w-nav-overlay');

    // Fix tabindex on menu links to make them clickable
    document.querySelectorAll('.nav-menu-wrapper .menu-link-block').forEach(link => {
        link.setAttribute('tabindex', '0');
        link.style.pointerEvents = 'auto';
    });

    navMenu.classList.add('open', 'w--nav-menu-open');
    navMenu.setAttribute('data-nav-menu-open', 'true');
    menuButton.classList.add('active', 'w--open');
    overlay.classList.add('open');
    document.body.style.overflow = 'hidden';
}

function closeMobileMenu() {
    const menuButton = document.querySelector('.menu-button');
    const navMenu = document.querySelector('.nav-menu-wrapper');
    const overlay = document.querySelector('.w-nav-overlay');

    navMenu.classList.remove('open', 'w--nav-menu-open');
    navMenu.removeAttribute('data-nav-menu-open');
    menuButton.classList.remove('active', 'w--open');
    overlay.classList.remove('open');
    document.body.style.overflow = '';
}

/* ========================================
   LANGUAGE SWITCHER
   ======================================== */

function initLanguageSwitcher() {
    const langButtons = document.querySelectorAll('.lang-btn');

    if (!langButtons.length) return;

    // Set initial active state
    const currentLang = i18n.getCurrentLanguage();
    langButtons.forEach(btn => {
        if (btn.dataset.lang === currentLang) {
            btn.classList.add('active');
        } else {
            btn.classList.remove('active');
        }
    });

    // Add click handlers
    langButtons.forEach(btn => {
        btn.addEventListener('click', function () {
            const newLang = this.dataset.lang;

            // Update active state
            langButtons.forEach(b => b.classList.remove('active'));
            this.classList.add('active');

            // Change language
            i18n.setLanguage(newLang);

        });
    });
}

/* ========================================
   ACTIVE PAGE HIGHLIGHTING
   ======================================== */

function highlightActivePage() {
    const currentPath = window.location.pathname;

    document.querySelectorAll('.menu-link-block').forEach(link => {
        const href = link.getAttribute('href');

        // Skip external links
        if (!href || href.startsWith('http') || href.startsWith('mailto') || href.startsWith('tel')) {
            return;
        }

        // Exact match
        if (href === currentPath) {
            link.classList.add('w--current');
        }
        // Partial match (for subpages)
        else if (currentPath.startsWith(href) && href !== '/nl' && href !== '/de') {
            link.classList.add('w--current');
        }
    });
}

/**
 * Header van mimugarten.nl: hamburger, en een uitklapmenu onder "Onze producten"
 * dat op muis opent en op touch pas bij de tweede tik doorlinkt.
 */
function initMgNav() {
    const toggle = document.querySelector('.mg-header__toggle');
    const main = document.querySelector('.mg-header__main');
    if (!toggle || !main) return;

    toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const open = main.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    main.querySelectorAll('.mg-header__nav > ul > li > a:not([aria-haspopup])').forEach(link => {
        link.addEventListener('click', () => {
            main.classList.remove('is-open');
            toggle.setAttribute('aria-expanded', 'false');
        });
    });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && main.classList.contains('is-open')) {
            main.classList.remove('is-open');
            toggle.setAttribute('aria-expanded', 'false');
        }
    });

    document.querySelectorAll('.mg-header__nav-has-sub').forEach(item => {
        const sub = item.querySelector('.mg-header__sub');
        const trigger = item.querySelector('a[aria-haspopup]');
        const caret = item.querySelector('.mg-header__nav-caret');
        if (!sub || !trigger) return;

        const open = () => {
            sub.removeAttribute('hidden');
            item.classList.add('is-open');
            trigger.setAttribute('aria-expanded', 'true');
        };
        const close = () => {
            sub.setAttribute('hidden', '');
            item.classList.remove('is-open');
            trigger.setAttribute('aria-expanded', 'false');
        };

        item.addEventListener('mouseenter', open);
        item.addEventListener('mouseleave', close);

        if (caret) {
            caret.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (item.classList.contains('is-open')) close(); else open();
            });
        }

        // Op touch opent de eerste tik het menu in plaats van door te linken.
        trigger.addEventListener('click', (e) => {
            if (window.matchMedia('(hover: none)').matches && !item.classList.contains('is-open')) {
                e.preventDefault();
                open();
            }
        });
        document.addEventListener('click', (e) => { if (!item.contains(e.target)) close(); });
        document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    });
}

/* ========================================
   QUOTE MODAL
   ======================================== */

function initQuoteModal() {
    const modal = document.getElementById('quote-modal');
    const quoteBtn = document.getElementById('quote-btn');
    const closeBtn = document.getElementById('quote-modal-close');
    const overlay = document.getElementById('quote-modal-overlay');
    const form = document.getElementById('quote-form-element');

    // Open modal
    if (quoteBtn) {
        quoteBtn.addEventListener('click', openQuoteModal);
    }

    // Close modal
    if (closeBtn) {
        closeBtn.addEventListener('click', closeQuoteModal);
    }

    if (overlay) {
        overlay.addEventListener('click', closeQuoteModal);
    }

    // Form submission
    if (form) {
        form.addEventListener('submit', handleQuoteSubmit);
    }
}

function openQuoteModal() {
    const modal = document.getElementById('quote-modal');
    const previewImg = document.getElementById('quote-preview-image');
    const submitBtn = document.querySelector('.quote-submit-btn-inline');
    const imageLoader = document.getElementById('quote-image-loader');
    const quoteOverlay = document.getElementById('quote-render-unavailable-overlay');

    if (modal) {
        modal.classList.add('open');
        document.body.style.overflow = 'hidden';

        // Re-translate modal content to ensure correct language
        if (window.i18n && typeof window.i18n.retranslate === 'function') {
            window.i18n.retranslate();
        }

        // GTM Event: Quote Modal Open
        const priceText = document.getElementById('simple-total-price')?.textContent || '0';
        const hasExtras = !!(state.markiezen || state.screens || state.vasteKozijnen);
        trackGTM('quote_open', {
            total_price: parseFloat(priceText.replace(/[.,]/g, '')) || 0,
            extras_selected: hasExtras
        });

        // Hide overlay initially (will show if render fails)
        if (quoteOverlay) {
            quoteOverlay.classList.remove('visible');
        }

        // Show loader
        if (imageLoader) {
            imageLoader.classList.add('show');
        }

        // Hide submit button
        if (submitBtn) {
            submitBtn.style.display = 'none';
        }

        // Update price
        updateQuoteModalPrices();
        setupConfigToggle();

        // REQUEST NEW RENDER with viewMode 0 (front view dag)

        // Build request string with viewMode 0
        const designType = state.verandaType == '1' ? '2' : state.finishingCap;
        let widthCm = Math.round(state.width * 100);
        let depthCm = Math.round(state.depth * 100);

        // Apply constraints
        if (widthCm < 300) widthCm = 300;
        if (widthCm > 1200) widthCm = 1200;
        if (depthCm < 250) depthCm = 250;
        if (depthCm > 500) depthCm = 500;

        // Altijd viewMode 0 (vooraanzicht, dag) en de hoogste kwaliteit die
        // deze tenant kent — dit beeld gaat mee in de offertemail.
        const quoteRequestString = buildRequestString({
            houseType: state.houseType,
            designType,
            color: state.color,
            covering: state.covering,
            widthCm,
            depthCm,
            led: state.led,
            leftWall: state.leftWall,
            rightWall: state.rightWall,
            frontWall: state.frontWall,
            viewMode: 0,
        }, 'hq');

        // Bump session id zodat een eventuele oude (niet-opgeruimde) hub-callback
        // van een vorige modal-open zijn UI-update niet alsnog doorduwt.
        state.quotePollingChainId++;
        state.quoteImageLoadingFor = null;
        const myChainId = state.quotePollingChainId;

        requestRender(quoteRequestString, {
            priority: 2, // iemand vraagt een offerte aan; gaat voor achtergrond-HQ
            onReady: (url, rs) => {
                if (myChainId !== state.quotePollingChainId) return;
                if (!previewImg) return;
                if (state.quoteImageLoadingFor === rs) return;
                state.quoteImageLoadingFor = rs;

                previewImg.onload = null;
                previewImg.onerror = null;

                previewImg.onload = () => {
                    if (myChainId !== state.quotePollingChainId) return;
                    if (quoteOverlay) quoteOverlay.classList.remove('visible');
                    if (imageLoader) imageLoader.classList.remove('show');
                    if (submitBtn) {
                        submitBtn.style.display = 'block';
                        submitBtn.disabled = false;
                        submitBtn.textContent = i18n.t('modal_submit_button');
                    }
                    state.quoteImageLoadingFor = null;
                };

                previewImg.onerror = () => {
                    if (myChainId !== state.quotePollingChainId) return;
                    console.warn(`[Render] quote-modal image load faalde voor ${rs} (${url})`);
                    state.quoteImageLoadingFor = null;
                    if (quoteOverlay) quoteOverlay.classList.add('visible');
                    if (imageLoader) imageLoader.classList.remove('show');
                    if (submitBtn) {
                        submitBtn.style.display = 'block';
                        submitBtn.disabled = false;
                        submitBtn.textContent = i18n.t('modal_submit_button');
                    }
                };

                // GEEN crossOrigin: de render-server (ws.cloudrenderer.eu) stuurt
                // geen Access-Control-Allow-Origin header. Met crossOrigin='anonymous'
                // weigert de browser het beeld te laden → onerror → overlay onterecht
                // zichtbaar terwijl de configurator prima werkt.
                previewImg.src = url;
                previewImg.style.opacity = '1';
            },
            onTimeout: (rs) => {
                if (myChainId !== state.quotePollingChainId) return;
                console.warn(`[Render] quote-modal render timeout voor ${rs}`);

                // Fallback: gebruik de hoofd-widget afbeelding als die er is.
                const widgetImg = document.getElementById('widget-preview-image');
                if (widgetImg && widgetImg.src && previewImg && state.quoteImageLoadingFor !== rs) {
                    previewImg.src = widgetImg.src;
                    previewImg.style.opacity = '1';
                }

                if (quoteOverlay) quoteOverlay.classList.add('visible');
                if (imageLoader) imageLoader.classList.remove('show');
                if (submitBtn) {
                    submitBtn.style.display = 'block';
                    submitBtn.disabled = false;
                    submitBtn.textContent = i18n.t('modal_submit_button');
                }
            }
        });
    }
}

function closeQuoteModal() {
    const modal = document.getElementById('quote-modal');

    if (modal) {
        modal.classList.remove('open');
        document.body.style.overflow = '';

        // Reset config toggle
        const configSummary = document.getElementById('quote-config-summary');
        const toggleBtn = document.getElementById('config-toggle-btn');
        if (configSummary) configSummary.style.display = 'none';
        if (toggleBtn) toggleBtn.classList.remove('active');

        // Invalidate any pending hub-callback from this modal-session so een
        // late ReceiveMessage of timeout niet alsnog de UI aanraakt.
        state.quotePollingChainId++;
        state.quoteImageLoadingFor = null;
    }
}

function setupConfigToggle() {
    const toggleBtn = document.getElementById('config-toggle-btn');
    const configSummary = document.getElementById('quote-config-summary');

    if (toggleBtn && configSummary) {
        // Remove old listeners
        const newToggleBtn = toggleBtn.cloneNode(true);
        toggleBtn.parentNode.replaceChild(newToggleBtn, toggleBtn);

        // Add new listener
        newToggleBtn.addEventListener('click', function () {
            const isHidden = configSummary.style.display === 'none';

            if (isHidden) {
                configSummary.style.display = 'block';
                newToggleBtn.classList.add('active');
                const hideText = i18n.t('modal_toggle_hide');
                newToggleBtn.querySelector('span').textContent = hideText;

                // On mobile, scroll to config summary
                const isMobile = window.innerWidth < 1024;
                if (isMobile) {
                    setTimeout(() => {
                        configSummary.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                }
            } else {
                configSummary.style.display = 'none';
                newToggleBtn.classList.remove('active');
                const showText = i18n.t('modal_toggle_details');
                newToggleBtn.querySelector('span').textContent = showText;
            }
        });
    }
}

function updateQuoteModalPrices() {
    // Get the actual calculated price from the display
    const simpleTotalPrice = document.getElementById('simple-total-price');
    const quoteTotalPrice = document.getElementById('quote-total-price');

    if (simpleTotalPrice && quoteTotalPrice) {
        // Use the real calculated price
        quoteTotalPrice.textContent = simpleTotalPrice.textContent.replace('€', '').replace(',-', '').trim();
    }

    // Update configuration summary in modal
    updateQuoteConfigurationSummary();
}

function updateQuoteConfigurationSummary() {
    const summaryContainer = document.getElementById('quote-config-summary');
    if (!summaryContainer) return;

    // Build configuration summary HTML with translations
    const colorMap = {
        '383e42': i18n.t('step3_color_anthracite'),
        'f1f0ea': i18n.t('step3_color_cream')
    };

    const coveringMap = {
        '0': i18n.t('step5_option_opaal'),
        '1': i18n.t('step5_option_helder'),
        '2': i18n.t('step5_option_glas')
    };

    const capMap = {
        '0': i18n.t('step4_option_modern'),
        '1': i18n.t('step4_option_classic')
    };

    const wallMap = {
        '0': i18n.t('step6_option_none'),
        '1': i18n.t('step6_option_glass_rail'),
        '2': i18n.t('step6_option_alu_rail'),
        '3': i18n.t('step6_option_rail_only')
    };

    const isPergamon = state.verandaType === '1';

    let html = `
        <h4 style="margin-bottom: 12px; color: #37424a; font-weight: 700;">${i18n.t('modal_config_title')}</h4>
        <table class="config-summary-table">
            <tr>
                <td><strong>${i18n.t('config_dimensions')}:</strong></td>
                <td>${state.width}m × ${state.depth}m</td>
            </tr>
            <tr>
                <td><strong>${i18n.t('config_type')}:</strong></td>
                <td>${isPergamon ? i18n.t('step2_option_pergamon') : i18n.t('step2_option_standaard')}</td>
            </tr>
    `;

    if (!isPergamon) {
        html += `
            <tr>
                <td><strong>${i18n.t('config_color')}:</strong></td>
                <td>${colorMap[state.color] || i18n.t('step3_color_anthracite')}</td>
            </tr>
            <tr>
                <td><strong>${i18n.t('config_finishing_cap')}:</strong></td>
                <td>${capMap[state.finishingCap] || i18n.t('step4_option_modern')}</td>
            </tr>
        `;
    }

    html += `
        <tr>
            <td><strong>${i18n.t('config_covering')}:</strong></td>
            <td>${coveringMap[state.covering] || i18n.t('step5_option_opaal')}</td>
        </tr>
        <tr>
            <td><strong>${i18n.t('config_led')}:</strong></td>
            <td>${state.led === '1' ? i18n.t('yes') : i18n.t('no')}</td>
        </tr>
        <tr>
            <td><strong>${i18n.t('config_left_wall')}:</strong></td>
            <td>${wallMap[state.leftWall] || i18n.t('step6_option_none')}</td>
        </tr>
        <tr>
            <td><strong>${i18n.t('config_right_wall')}:</strong></td>
            <td>${wallMap[state.rightWall] || i18n.t('step7_option_none')}</td>
        </tr>
        <tr>
            <td><strong>${i18n.t('config_front_wall')}:</strong></td>
            <td>${wallMap[state.frontWall] || i18n.t('step8_option_none')}</td>
        </tr>
    `;

    // Opties buiten prijsopgave
    const hasExtraOptions = state.markiezen || state.screens || state.vasteKozijnen;
    if (hasExtraOptions) {
        html += `
            <tr>
                <td colspan="2" style="padding-top: 12px;">
                    <strong style="color: #f59e0b;">${i18n.t('config_options_outside_quote')}</strong>
                </td>
            </tr>
        `;

        if (state.markiezen) {
            html += `<tr><td colspan="2" style="padding-left: 20px;">• ${i18n.t('step9_markiezen')}</td></tr>`;
        }
        if (state.screens) {
            html += `<tr><td colspan="2" style="padding-left: 20px;">• ${i18n.t('step9_screens')}</td></tr>`;
        }
        if (state.vasteKozijnen) {
            html += `<tr><td colspan="2" style="padding-left: 20px;">• ${i18n.t('step9_vaste_kozijnen')}</td></tr>`;
        }
    }

    html += `</table>`;

    summaryContainer.innerHTML = html;
}

async function handleQuoteSubmit(e) {
    e.preventDefault();

    const formData = new FormData(e.target);
    const submitBtn = document.querySelector('.quote-submit-btn-inline');
    const originalText = submitBtn ? submitBtn.textContent : i18n.t('modal_submit_button');

    // Disable button and show loading
    if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = i18n.t('modal_submit_loading');
    }

    try {
        // Check if render is available by checking the overlay visibility
        const quoteOverlay = document.getElementById('quote-render-unavailable-overlay');
        const isRenderAvailable = !quoteOverlay || !quoteOverlay.classList.contains('visible');

        // Try to capture image, but it might be null if render failed
        const imageData = isRenderAvailable ? await captureRenderImage() : null;

        // Get current price from widget or simple display
        const widgetPrice = document.getElementById('widget-total-price');
        const simplePrice = document.getElementById('simple-total-price');
        const priceElement = widgetPrice || simplePrice;

        // Parse Dutch price format: € 3.156,66,-
        // Remove €, spaces, and trailing ,-
        // Then replace comma with dot for decimal, remove dots (thousand separator)
        let priceText = priceElement ? priceElement.textContent : '0';
        priceText = priceText.replace(/€/g, '').replace(/\s/g, '').replace(/,-/g, '');
        // Replace comma (decimal) with dot, then remove dots (thousand separators)
        priceText = priceText.replace(/\./g, '').replace(/,/g, '.');
        const price = parseFloat(priceText) || 0;

        // Prepare configuration data
        const configuration = {
            width: state.width,
            depth: state.depth,
            verandaType: state.verandaType,
            color: state.color,
            finishingCap: state.finishingCap,
            covering: state.covering,
            led: state.led,
            leftWall: state.leftWall,
            rightWall: state.rightWall,
            frontWall: state.frontWall,
            houseType: state.houseType || '2',  // Default Vrijstaand
            markiezen: state.markiezen || false,
            screens: state.screens || false,
            vasteKozijnen: state.vasteKozijnen || false
        };

        // Generate share link
        const shareLink = generateShareLink(state);

        // Generate QR code in frontend
        let qrCodeData = null;
        try {
            qrCodeData = await QRCode.toDataURL(shareLink, {
                width: 300,
                margin: 2,
                color: {
                    dark: '#000000',
                    light: '#FFFFFF'
                }
            });
        } catch (qrError) {
            // Continue without QR code
        }

        // Try PHP script first (for production hosting), fallback to API (for preview)
        let response;
        const emailData = {
            customer_email: formData.get('email'),
            customer_name: formData.get('name'),
            customer_phone: formData.get('phone'),
            customer_message: formData.get('message') || '',
            customer_address: formData.get('address') || '',
            customer_zipcode: formData.get('zipcode') || '',
            customer_city: formData.get('city') || '',
            customer_promo_code: formData.get('promo_code') || '',
            price_is_fallback: state.pricingError || false,
            configuration: configuration,
            price: price,
            image_data: imageData,
            render_available: isRenderAvailable,  // Flag to indicate if render is available
            qr_code_data: qrCodeData,  // Send QR code from frontend
            language: i18n.getCurrentLanguage()  // Add current language (nl or de)
        };

        // Send email via PHP script (production ready)
        try {
            const response = await fetch('./send-quote.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(emailData)
            });

            if (!response.ok) {
                throw new Error('Failed to send email');
            }

            const result = await response.json();

            // GTM Event: Quote Submit Success
            trackGTM('quote_submit', {
                status: 'success',
                total_price: price
            });

            // Conversie-tracking, pas nadat de aanvraag echt geslaagd is.
            // Welke pixels er af mogen staat in tenant.config.js; is er niets
            // ingericht, dan gebeurt hier ook niets.
            try {
                if (tenant.tracking.metaPixelLead && typeof window.fbq === 'function') {
                    window.fbq('track', 'Lead', {
                        value: price,
                        currency: 'EUR',
                        content_name: 'Configurator quote request'
                    });
                }
                if (tenant.tracking.googleAdsConversion && typeof window.gtag === 'function') {
                    window.gtag('event', 'conversion', {
                        value: price,
                        currency: 'EUR',
                        send_to: tenant.tracking.googleAdsConversion
                    });
                }
            } catch (_) {
                // Een fout in tracking mag de bezoeker nooit raken.
            }

            // Show success message
            showSuccessOverlay(i18n.t('modal_submit_success'));

            // Close modal and reset form
            setTimeout(() => {
                closeQuoteModal();
                e.target.reset();
            }, 2000);

        } catch (error) {
            // GTM Event: Quote Submit Error
            trackGTM('quote_submit', {
                status: 'error',
                error_message: error.message
            });

            showErrorOverlay(i18n.t('modal_submit_error'));
        }

    } catch (error) {
        // GTM Event: Quote Submit Error
        trackGTM('quote_submit', {
            status: 'error',
            error_message: error.message
        });

        showErrorOverlay('Er is iets misgegaan. Probeer het opnieuw.');

        // Re-enable button
        if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = originalText;
        }
    }
}

// Custom success overlay
function showSuccessOverlay(message) {
    const overlay = document.createElement('div');
    overlay.className = 'custom-alert-overlay';
    overlay.innerHTML = `
        <div class="custom-alert-box success">
            <div class="custom-alert-icon">✓</div>
            <div class="custom-alert-message">${message}</div>
            <button onclick="this.closest('.custom-alert-overlay').remove()" class="custom-alert-close">${i18n.t('modal_success_close') || 'Sluiten'}</button>
        </div>
    `;
    document.body.appendChild(overlay);
}

// Custom error overlay
function showErrorOverlay(message) {
    const overlay = document.createElement('div');
    overlay.className = 'custom-alert-overlay';
    overlay.innerHTML = `
        <div class="custom-alert-box error">
            <div class="custom-alert-icon">✕</div>
            <div class="custom-alert-message">${message}</div>
            <button onclick="this.closest('.custom-alert-overlay').remove()" class="custom-alert-close">${i18n.t('modal_error_close')}</button>
        </div>
    `;
    document.body.appendChild(overlay);
}

// Add CSS for custom alerts
if (!document.getElementById('custom-alert-styles')) {
    const style = document.createElement('style');
    style.id = 'custom-alert-styles';
    style.textContent = `
        .custom-alert-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(55, 66, 74, 0.85);
            backdrop-filter: blur(8px);
            z-index: 3000;
            display: flex;
            align-items: center;
            justify-content: center;
            animation: fadeIn 0.3s;
            transition: opacity 0.3s;
        }
        .custom-alert-box {
            background: white;
            padding: 2rem;
            max-width: 400px;
            text-align: center;
            box-shadow: 0 20px 60px rgba(0,0,0,0.4);
        }
        .custom-alert-box.success {
            border-top: 4px solid var(--brand);
        }
        .custom-alert-box.error {
            border-top: 4px solid #dc2626;
        }
        .custom-alert-icon {
            width: 60px;
            height: 60px;
            margin: 0 auto 1rem;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 2rem;
            font-weight: 700;
        }
        .custom-alert-box.success .custom-alert-icon {
            background: #dcfce7;
            color: var(--brand);
        }
        .custom-alert-box.error .custom-alert-icon {
            background: #fee2e2;
            color: #dc2626;
        }
        .custom-alert-message {
            font-size: 1.125rem;
            color: #37424a;
            font-weight: 600;
            margin-bottom: 1rem;
        }
        .custom-alert-close {
            padding: 0.75rem 2rem;
            background: linear-gradient(135deg, var(--brand-light) 0%, var(--brand) 100%);
            color: white;
            border: none;
            border-radius: 0.5rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
        }
        .custom-alert-close:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(var(--brand-rgb), 0.3);
        }
        @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
        }
    `;
    document.head.appendChild(style);
}

/**
 * Update wizard steps visibility based on verandaType
 * Pergamon: stap 3 (Kleur) en 4 (Afwerkingskap) zijn gedisabled — pergamon
 * bestaat alleen in antraciet.
 */
function updateWizardStepsForVerandaType() {
    const stepItems = document.querySelectorAll('.wizard-step-item');
    const isPergamon = state.verandaType === '1';

    // Step 3 (Kleur) and Step 4 (Afwerkingskap) — index 2 and 3
    [2, 3].forEach(idx => {
        if (!stepItems[idx]) return;
        if (isPergamon) {
            stepItems[idx].classList.add('disabled');
            stepItems[idx].style.opacity = '0.4';
            stepItems[idx].style.pointerEvents = 'none';
        } else {
            stepItems[idx].classList.remove('disabled');
            stepItems[idx].style.opacity = '';
            stepItems[idx].style.pointerEvents = '';
        }
    });
}

/* ========================================
   WIZARD NAVIGATION
   ======================================== */

window.wizardNext = function () {
    if (state.activeStep < 9) {
        let nextStep = state.activeStep + 1;

        // Pergamon: stap 3 (Kleur) én 4 (Afwerkingskap) overslaan.
        if (state.verandaType === '1') {
            if (state.activeStep === 2) nextStep = 5; // 2 → 5 (skip 3 en 4)
            if (state.activeStep === 3) nextStep = 5; // safety
            if (state.activeStep === 4) nextStep = 5; // safety
        }

        wizardGoToStep(nextStep);
    }
};

window.wizardPrev = function () {
    if (state.activeStep > 1) {
        let prevStep = state.activeStep - 1;

        // Pergamon: stap 3 én 4 overslaan in beide richtingen.
        if (state.verandaType === '1') {
            if (state.activeStep === 5) prevStep = 2; // 5 → 2 (skip 4 en 3)
            if (state.activeStep === 4) prevStep = 2; // safety
            if (state.activeStep === 3) prevStep = 2; // safety
        }

        wizardGoToStep(prevStep);
    }
};

window.wizardGoToStep = function (stepNumber) {
    if (stepNumber < 1 || stepNumber > 9) return;

    const previousStep = state.activeStep;
    let shouldRenderImage = false;

    // GTM Event: Wizard Step View
    trackGTM('wizard_step_view', {
        step_number: stepNumber,
        verandaType: state.verandaType,
        covering: state.covering,
        night: state.night,
        width: state.width,
        depth: state.depth
    });

    // Step 9: Altijd nachtmode
    if (stepNumber === 9) {
        state.night = true;
        updateDayNightButtons();
        shouldRenderImage = true;
    }

    // Alle andere stappen: Altijd dagmode
    if (stepNumber !== 9 && previousStep === 9) {
        state.night = false;
        updateDayNightButtons();
        shouldRenderImage = true;
    }

    // CRITICAL: Remove active class from ALL panels first
    document.querySelectorAll('.wizard-step-panel').forEach(panel => {
        panel.classList.remove('active');
    });

    // Remove active from ALL stepper items
    document.querySelectorAll('.wizard-step-item').forEach(item => {
        item.classList.remove('active');
        if (parseInt(item.dataset.step) < stepNumber) {
            item.classList.add('completed');
        } else {
            item.classList.remove('completed');
        }
    });

    // Update state
    state.activeStep = stepNumber;

    // Show new step
    const newPanel = document.getElementById(`wizard-step-${stepNumber}`);
    if (newPanel) {
        newPanel.classList.add('active');
    }

    // Update stepper
    const stepperItem = document.querySelector(`.wizard-step-item[data-step="${stepNumber}"]`);
    if (stepperItem) {
        stepperItem.classList.add('active');
    }

    // Update buttons
    updateWizardButtons();

    // Sync form values with state
    syncWizardFormWithState(stepNumber);

    // Always trigger viewMode selection for the new step to show correct camera
    triggerViewModeForStep(stepNumber);

    // Generate new render image if night mode changed or always for step transitions
    if (shouldRenderImage || previousStep !== stepNumber) {
        generateImage();
    }

    // Scroll to top of wizard content
    document.querySelector('.wizard-content')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
};

/**
 * Trigger the correct viewMode for each step based on default or selected values
 */
function triggerViewModeForStep(stepNumber) {
    switch (stepNumber) {
        case 1: // Afmetingen
            autoSelectViewMode('width');
            break;
        case 2: // Type veranda
            autoSelectViewMode('verandaType');
            break;
        case 3: // Kleur
            autoSelectViewMode('color');
            break;
        case 4: // Afwerkingskap
            autoSelectViewMode('finishingCap');
            break;
        case 5: // Dakplaat
            autoSelectViewMode('covering');
            break;
        case 6: // Linker zijde
            autoSelectViewMode('leftWall');
            break;
        case 7: // Rechter zijde
            autoSelectViewMode('rightWall');
            break;
        case 8: // Voorzijde
            autoSelectViewMode('frontWall');
            break;
        case 9: // Extra opties
            autoSelectViewMode('led');
            break;
    }
}

function updateWizardButtons() {
    const prevButtons = document.querySelectorAll('.wizard-btn-prev');
    const nextButtons = document.querySelectorAll('.wizard-btn-next');

    prevButtons.forEach(btn => {
        btn.disabled = state.activeStep === 1;
    });
}

function syncWizardFormWithState(stepNumber) {
    // Sync the form inputs with the state for the current step
    const currentPanel = document.getElementById(`wizard-step-${stepNumber}`);
    if (!currentPanel) return;

    // For radio buttons
    currentPanel.querySelectorAll('input[type="radio"]').forEach(input => {
        const name = input.name;
        if (state[name] !== undefined && input.value === state[name]) {
            input.checked = true;
        }
    });

    // For number inputs (dimensions)
    if (stepNumber === 2) {
        const widthInput = document.getElementById('wizard-width');
        const depthInput = document.getElementById('wizard-depth');
        if (widthInput) widthInput.value = state.width;
        if (depthInput) depthInput.value = state.depth;
    }
}

window.wizardComplete = function () {

    // Generate final image
    generateImage();

    // Scroll to preview
    document.getElementById('configurator')?.scrollIntoView({ behavior: 'smooth' });

    // Maybe show a success message or open quote modal automatically
    setTimeout(() => {
        alert('Uw configuratie is compleet! Bekijk het resultaat in de preview.');
    }, 500);
};

// Initialize wizard form bindings
function initWizardForm() {
    // CRITICAL: Ensure only step 1 is active on initialization
    document.querySelectorAll('.wizard-step-panel').forEach((panel, index) => {
        if (index === 0) {
            panel.classList.add('active');
        } else {
            panel.classList.remove('active');
        }
    });

    // Set first stepper item as active
    document.querySelectorAll('.wizard-step-item').forEach((item, index) => {
        if (index === 0) {
            item.classList.add('active');
        } else {
            item.classList.remove('active', 'completed');
        }
    });

    // Ensure state starts at step 1
    state.activeStep = 1;

    // Initialize wizard steps visibility based on initial verandaType
    updateWizardStepsForVerandaType();

    // Bind all wizard radio inputs
    document.querySelectorAll('.wizard-step-panel input[type="radio"]').forEach(input => {
        input.addEventListener('change', function () {
            const name = this.name;
            state[name] = this.value;

            // Update wizard steps visibility when verandaType changes
            if (name === 'verandaType') {
                updateWizardStepsForVerandaType();
            }

            // Auto-select viewMode and calculate price
            autoSelectViewMode(name);
            calculatePrice();

            // No auto-advance - user manually clicks "Volgende"
        });
    });

    // Bind LED checkbox
    const ledCheckbox = document.getElementById('led-checkbox');
    if (ledCheckbox) {
        ledCheckbox.addEventListener('change', function () {
            state.led = this.checked ? '1' : '0';

            // Update night mode when LED changes (LED uses night view)
            if (state.led === '1') {
                state.night = true;
            } else {
                state.night = false;
            }
            updateDayNightButtons();

            autoSelectViewMode('led');
            calculatePrice();

            // Generate new render with LED change
            generateImage();
        });

        // Set initial state
        ledCheckbox.checked = state.led === '1';
    }

    // Bind Markiezen checkbox
    const markiezenCheckbox = document.getElementById('markiezen-checkbox');
    if (markiezenCheckbox) {
        markiezenCheckbox.addEventListener('change', function () {
            state.markiezen = this.checked;
            // Note: No price impact, no image generation needed
        });

        // Set initial state
        markiezenCheckbox.checked = state.markiezen || false;
    }

    // Bind Screens checkbox
    const screensCheckbox = document.getElementById('screens-checkbox');
    if (screensCheckbox) {
        screensCheckbox.addEventListener('change', function () {
            state.screens = this.checked;
            // Note: No price impact, no image generation needed
        });

        // Set initial state
        screensCheckbox.checked = state.screens || false;
    }

    // Bind Vaste Kozijnen checkbox
    const vasteKozijnenCheckbox = document.getElementById('vaste-kozijnen-checkbox');
    if (vasteKozijnenCheckbox) {
        vasteKozijnenCheckbox.addEventListener('change', function () {
            state.vasteKozijnen = this.checked;
            // Note: No price impact, no image generation needed
        });

        // Set initial state
        vasteKozijnenCheckbox.checked = state.vasteKozijnen || false;
    }

    // Bind dimension inputs
    const widthInput = document.getElementById('wizard-width');
    const depthInput = document.getElementById('wizard-depth');

    if (widthInput) {
        widthInput.addEventListener('input', function () {
            // Replace comma with dot for decimal notation
            let value = this.value.replace(',', '.');
            this.value = value;

            // Parse and validate (but don't round the display value)
            const parsed = parseFloat(value);
            if (!isNaN(parsed)) {
                state.width = Math.max(3, Math.min(12, parsed));
                autoSelectViewMode('width');
                calculatePrice();
            }
        });

        widthInput.addEventListener('blur', function () {
            // On blur, ensure value is within bounds
            const parsed = parseFloat(this.value.replace(',', '.'));
            if (isNaN(parsed) || parsed < 3 || parsed > 12) {
                this.value = state.width;
            }
        });
    }

    if (depthInput) {
        depthInput.addEventListener('input', function () {
            // Replace comma with dot for decimal notation
            let value = this.value.replace(',', '.');
            this.value = value;

            // Parse and validate (but don't round the display value)
            const parsed = parseFloat(value);
            if (!isNaN(parsed)) {
                state.depth = Math.max(2.5, Math.min(5, parsed));
                autoSelectViewMode('depth');
                calculatePrice();
            }
        });

        depthInput.addEventListener('blur', function () {
            // On blur, ensure value is within bounds
            const parsed = parseFloat(this.value.replace(',', '.'));
            if (isNaN(parsed) || parsed < 2.5 || parsed > 5) {
                this.value = state.depth;
            }
        });
    }
}

/**
 * Move wizard stepper to widget on mobile
 * On mobile: stepper goes inside widget (after image, before price)
 * On desktop: stepper stays in wizard-right
 */
function moveStepperToWidgetOnMobile() {
    const stepper = document.querySelector('.wizard-stepper');
    const widgetInfo = document.querySelector('.widget-info');
    const widgetContent = document.querySelector('.widget-content');
    const wizardRight = document.querySelector('.wizard-right');

    if (!stepper || !widgetInfo || !widgetContent || !wizardRight) {
        console.warn('Missing elements for stepper move');
        return;
    }

    const isMobile = window.innerWidth < 1024;

    if (isMobile) {
        // Move stepper to widget (before widget-info, after widget-preview)
        if (stepper.parentElement !== widgetContent) {
            widgetContent.insertBefore(stepper, widgetInfo);
            stepper.classList.add('in-widget');
        } else {
        }
    } else {
        // Move stepper back to wizard-right
        if (!wizardRight.contains(stepper)) {
            wizardRight.appendChild(stepper);
            stepper.classList.remove('in-widget');
        } else {
        }
    }
}

/* ========================================
   SHARE LINK & URL CONFIGURATION
   ======================================== */

function updateAllInputsFromState() {
    // Update all form inputs to reflect current state
    const inputs = {
        width: document.querySelector('input[name="width"]'),
        depth: document.querySelector('input[name="depth"]'),
        houseType: document.querySelector(`input[name="houseType"][value="${state.houseType}"]`),
        verandaType: document.querySelector(`input[name="verandaType"][value="${state.verandaType}"]`),
        color: document.querySelector(`input[name="color"][value="${state.color}"]`),
        covering: document.querySelector(`input[name="covering"][value="${state.covering}"]`),
        finishingCap: document.querySelector(`input[name="finishingCap"][value="${state.finishingCap}"]`),
        led: document.querySelector(`input[name="led"][value="${state.led}"]`),
        leftWall: document.querySelector(`input[name="leftWall"][value="${state.leftWall}"]`),
        rightWall: document.querySelector(`input[name="rightWall"][value="${state.rightWall}"]`),
        frontWall: document.querySelector(`input[name="frontWall"][value="${state.frontWall}"]`)
    };

    // Update text inputs
    if (inputs.width) inputs.width.value = state.width;
    if (inputs.depth) inputs.depth.value = state.depth;

    // Check radio buttons
    Object.keys(inputs).forEach(key => {
        if (inputs[key] && inputs[key].type === 'radio') {
            inputs[key].checked = true;
        }
    });
}

function showToast(message, type = 'info') {
    // Simple toast notification
    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;
    toast.textContent = message;
    toast.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        padding: 15px 25px;
        background: ${type === 'success' ? 'var(--brand)' : type === 'error' ? '#dc2626' : '#37424a'};
        color: white;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        z-index: 10000;
        font-weight: 600;
        animation: slideIn 0.3s ease-out;
    `;

    document.body.appendChild(toast);

    setTimeout(() => {
        toast.style.animation = 'slideOut 0.3s ease-out';
        setTimeout(() => toast.remove(), 300);
    }, 3000);
}

// Add CSS animations for toast
if (!document.getElementById('toast-styles')) {
    const style = document.createElement('style');
    style.id = 'toast-styles';
    style.textContent = `
        @keyframes slideIn {
            from { transform: translateX(400px); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideOut {
            from { transform: translateX(0); opacity: 1; }
            to { transform: translateX(400px); opacity: 0; }
        }
    `;
    document.head.appendChild(style);
}